'use client';

import React from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Building2,
  Calendar,
  FileText,
  Link2,
  MapPin,
  QrCode,
  ShieldCheck,
  User,
} from 'lucide-react';
import type { PublicRecord } from '@/types/publicRecords';
import { PILLAR_TOKENS, STATUS_TOKENS } from '@/types/publicRecords';
import {
  CERTIFICATE_DISCLAIMER,
  CERTIFICATE_INTRO,
  getCertificateId,
  getCertificateTitle,
  getProofCardId,
  getProofSummaryTitle,
  getRecordDisplayLabel,
  getTrustLevel,
  hasProofSummary,
  PROOF_SUMMARY_DISCLAIMER,
  PROOF_SUMMARY_INTRO,
  TRUST_LEVEL_LABELS,
  type ProofDocumentKind,
} from '@/lib/recordTrust';
import RecordReviewTrail from '@/components/shared/RecordReviewTrail';
import CertificateDocument from '@/components/certificate/CertificateDocument';
import PublicCertificateToolbar from '@/components/certificate/PublicCertificateToolbar';
import LandingNav from '@/app/public-landing-page/components/LandingNav';
import LandingFooter from '@/app/public-landing-page/components/LandingFooter';
import AppLogo from '@/components/ui/AppLogo';
import DocumentTopNav from '@/components/public-archive/DocumentTopNav';
import { getPublicRecordImage } from '@/lib/publicRecordMedia';

type Props = {
  record: PublicRecord;
  kind: ProofDocumentKind;
};

export default function PublicTrustDocumentView({ record, kind }: Props) {
  const pillar = PILLAR_TOKENS[record.pillar];
  const status = STATUS_TOKENS[record.verificationStatus];
  const isCertificate = kind === 'certificate';
  const isGold =
    isCertificate &&
    (record.verificationStatus === 'Gold Verified' ||
      record.verificationStatus === 'Institution Verified');
  const docId = isCertificate ? getCertificateId(record)! : getProofCardId(record);
  const verifyPath = `/verify?id=${encodeURIComponent(docId)}`;
  const pageTitle = isCertificate ? getCertificateTitle(record) : 'Ertzyx Public Proof Summary';
  const title = isCertificate ? getCertificateTitle(record) : getProofSummaryTitle(record);
  const intro = isCertificate ? CERTIFICATE_INTRO : PROOF_SUMMARY_INTRO;
  const disclaimer = isCertificate ? CERTIFICATE_DISCLAIMER : PROOF_SUMMARY_DISCLAIMER;
  const trust = TRUST_LEVEL_LABELS[getTrustLevel(record)];
  const stripeClass = isCertificate ? 'border-archive-gold/30' : 'border-cyan-400/35';
  const headerGradient = isCertificate
    ? 'from-archive-gold-pale via-white to-rose-50'
    : 'from-cyan-50 via-white to-slate-50';
  const topBar = isCertificate ? 'bg-archive-gold' : 'bg-[var(--public-sapphire)]';

  if (isCertificate) {
    return (
      <div className="public-page public-family-verify min-h-screen">
        <div className="certificate-no-print">
          <LandingNav />
        </div>
        <main className="relative overflow-hidden pb-20 pt-6">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background:
                'radial-gradient(circle at 18% 12%, rgba(201,168,76,0.22), transparent 40%)',
            }}
          />
          <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
            <Link
              href="/"
              className="certificate-no-print mb-6 inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-trust-blue"
            >
              <ArrowLeft size={14} />
              Back to landing
            </Link>
            <PublicCertificateToolbar record={record} />
            <CertificateDocument record={record} />
            <p className="certificate-no-print mt-6 text-center text-xs leading-relaxed text-slate-500">
              {disclaimer}
            </p>
            <p className="certificate-no-print mt-2 text-center text-[11px] leading-relaxed text-slate-400">
              Verification reflects the current record status available through Ertzyx. It does not
              represent Government of Canada approval, PSPC certification, or independent legal
              certification unless explicitly stated by the issuing organization.
            </p>
          </div>
        </main>
        <div className="certificate-no-print">
          <LandingFooter />
        </div>
      </div>
    );
  }

  const coverImage = getPublicRecordImage(record);

  return (
    <div className="public-page public-family-verify min-h-screen">
      <DocumentTopNav
        backLabel="Back to Explore"
        backHref="/individual-dashboard/explore?mode=archives"
        title={pageTitle}
      />
      <main className="relative overflow-hidden pb-20 pt-16">
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="relative mb-6 h-44 overflow-hidden rounded-2xl sm:h-52">
            <img src={coverImage} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/20 to-transparent" />
          </div>

          <div
            className={`relative overflow-hidden rounded-3xl border-2 ${stripeClass} bg-white shadow-2xl`}
          >
            <div className={`h-2 w-full ${topBar}`} />

            <div className="p-6 sm:p-8">
              <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-trust-blue">
                    <AppLogo size={36} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-trust-blue">Ertzyx</p>
                    <p className="text-xs text-[#001E5A]/70">{pageTitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1.5">
                  <div className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
                  <span className="text-xs font-semibold text-green-700">Review on file</span>
                </div>
              </div>

              <div
                className={`mb-6 rounded-2xl border p-5 bg-gradient-to-br ${headerGradient} ${stripeClass}`}
              >
                <div className="mb-4 flex items-start justify-between gap-3">
                  <div>
                    <p
                      className={`text-xs font-bold uppercase tracking-[0.18em] ${isCertificate ? 'text-amber-700' : 'text-cyan-700'}`}
                    >
                      {title}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-[#001E5A]/75">{intro}</p>
                  </div>
                  <div
                    className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-white/85 shadow-lg ring-1 ${
                      isGold ? 'ring-archive-gold/40' : 'ring-cyan-300/50'
                    }`}
                  >
                    <AppLogo size={42} />
                  </div>
                </div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                  Record
                </p>
                <h1
                  data-clarity-mask="true"
                  className="mb-2 text-2xl font-bold text-[#002B7F] sm:text-3xl"
                >
                  {record.title}
                </h1>
                <p data-clarity-mask="true" className="text-sm leading-relaxed text-[#001E5A]">
                  {record.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span
                    className="rounded-full px-3 py-1 text-xs font-bold"
                    style={{
                      color: status.color,
                      background: status.bg,
                      border: `1px solid ${status.color}35`,
                    }}
                  >
                    {status.label}
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-xs font-bold"
                    style={{
                      background: pillar.bg,
                      color: pillar.text,
                      border: `1px solid ${pillar.icon}40`,
                    }}
                  >
                    {record.pillar}
                  </span>
                  <span
                    className="rounded-full px-3 py-1 text-xs font-bold"
                    style={{ color: trust.color, background: trust.bg }}
                  >
                    {getRecordDisplayLabel(record)}
                  </span>
                  {isCertificate && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800">
                      Certificate Issued
                    </span>
                  )}
                </div>
              </div>

              <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-4">
                  <div
                    className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-white ${
                      record.ownerType === 'institution'
                        ? 'bg-gradient-to-br from-violet-600 to-amber-400'
                        : 'bg-gradient-to-br from-teal-600 to-cyan-500'
                    }`}
                  >
                    {record.ownerType === 'institution' ? (
                      <Building2 size={20} />
                    ) : (
                      <User size={20} />
                    )}
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                      {record.ownerType === 'institution' ? 'Institution' : 'Individual'}
                    </p>
                    <p data-clarity-mask="true" className="text-sm font-semibold text-[#002B7F]">
                      {record.ownerName}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-4 text-sm text-[#002B7F]">
                  <Meta icon={<Calendar size={14} />} label="Date">
                    {new Date(record.date).toLocaleDateString('en-CA', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Meta>
                  <Meta icon={<MapPin size={14} />} label="Location">
                    <span data-clarity-mask="true">{record.locationName}</span>
                  </Meta>
                  <Meta icon={<FileText size={14} />} label="Evidence">
                    {record.evidenceCount} items on file
                  </Meta>
                </div>
              </div>

              <div
                className={`rounded-xl border-2 p-4 ${isCertificate ? 'border-archive-gold/40' : 'border-cyan-400/30'}`}
                style={{
                  background: isCertificate
                    ? 'linear-gradient(135deg, rgba(201,168,76,0.08), rgba(16,185,129,0.06))'
                    : 'linear-gradient(135deg, rgba(34,211,238,0.06), rgba(241,245,249,0.9))',
                }}
              >
                <div className="flex items-start gap-3">
                  <AppLogo size={24} />
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#001E5A]/60">
                      Verification context
                    </p>
                    <p className="text-sm font-medium text-[#002B7F]">
                      Structured metadata, evidence files, and review steps are preserved on Ertzyx
                      Ledger.
                    </p>
                    <p
                      data-clarity-mask="true"
                      className="mt-1 font-mono text-xs text-[#001E5A]/60"
                    >
                      {docId}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <Link2 size={14} className="flex-shrink-0 text-slate-400" />
                <span
                  data-clarity-mask="true"
                  className="truncate font-mono text-xs text-slate-500"
                >
                  ertzyx.com{verifyPath}
                </span>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4">
                <QrCode size={36} className="text-slate-400" />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Verify
                  </p>
                  <p data-clarity-mask="true" className="font-mono text-xs text-slate-600">
                    {docId}
                  </p>
                </div>
              </div>

              <div className="mb-6 rounded-xl border border-slate-100 bg-white p-4">
                <RecordReviewTrail record={record} compact />
              </div>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                <Link
                  href={verifyPath}
                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-trust-blue px-5 py-3 text-sm font-semibold text-white"
                >
                  <ShieldCheck size={16} />
                  Verify on Ertzyx
                </Link>
                {isCertificate && hasProofSummary(record) && (
                  <Link
                    href={`/proof-card/${record.id}`}
                    className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-cyan-400/40 bg-cyan-50 px-5 py-3 text-sm font-semibold text-cyan-800"
                  >
                    View proof summary
                  </Link>
                )}
              </div>

              <p className="mt-6 text-center text-[11px] leading-relaxed text-slate-500">
                {disclaimer}
              </p>
              <p className="mt-2 text-center text-[10px] leading-relaxed text-slate-400">
                Verification reflects the current record status available through Ertzyx. It does
                not represent Government of Canada approval, PSPC certification, or independent
                legal certification unless explicitly stated by the issuing organization.
              </p>
            </div>
          </div>
        </div>
      </main>
      <LandingFooter />
    </div>
  );
}

function Meta({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-start gap-2 last:mb-0">
      <span className="mt-0.5 text-slate-400">{icon}</span>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#001E5A]/55">{label}</p>
        <p className="text-sm font-medium">{children}</p>
      </div>
    </div>
  );
}
